import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactHookForm } from '../../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../../hooks/useReactHookForm';
import { useCallback, useEffect, useState } from 'react';
import { useSaveData } from '../../../../hooks/useSaveData';
import { FileUploaded } from '../../../../models/fileUpload';
import { fileUploadApiKeyStore } from '../../../../api/file-upload-key-store';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { FormInput } from '../../../../components/hook-form/form-input.component';
import { useDropzone } from 'react-dropzone';
import { useAuthorize } from '../../../../hooks/use-authorize';
import { UserPermissionName } from '../../../../models/user-permission-name';

const styling = {
  dropZone: {
    padding: '27px',
    border: '0.1em dashed #99A3A4',
    backgroundColor: '#F2F3F4',
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
  },
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
};

interface FileRequestArgs {
  projectID?: number;
  officeID?: number;
  customerID?: number;
  tempEntityID?: any;
}

interface EditFileUploadDialog {
  fileData: any;
  fileRequestArgs: FileRequestArgs;
  onUpdate: () => void;
  onClosed: () => void;
}
export const EditFileUploadDialog = ({ fileData, fileRequestArgs, onUpdate, onClosed }: EditFileUploadDialog) => {
  const form = useReactHookForm({
    defaultValues: { ...fileData },
  });
  const [files, setFiles] = useState<any>();
  const { isLoading, mutateAsync } = useSaveData<FileUploaded>(fileUploadApiKeyStore.updateUploadFile(), () =>
    fileUploadApiKeyStore.getFiles(fileRequestArgs)
  );

  const handleDrop = useCallback((acceptedFiles: any) => {
    setFiles(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
  });

  const handleSubmit = async (value: any) => {
    if (files || fileData) {
      const formData = new FormData();
      formData.append('fileID', value?.fileID);
      formData.append('UploadedFile', files ?? '');
      formData.append('FileName', files ? files?.name : value?.fileName);
      formData.append('FileType', files ? files?.type : value?.fileType);
      formData.append('Description', value.description ?? '');
      formData.append('FileSection', 'office');

      if (fileRequestArgs) {
        if (fileRequestArgs.projectID) {
          formData.append('ProjectID', fileRequestArgs.projectID.toString());
        }
        if (fileRequestArgs.officeID) {
          formData.append('OfficeID', fileRequestArgs.officeID.toString());
        }
        if (fileRequestArgs.customerID) {
          formData.append('CustomerID', fileRequestArgs.customerID.toString());
        }
        if (fileRequestArgs.tempEntityID) {
          formData.append('TempEntityID', fileRequestArgs.tempEntityID.toString());
        }
      }

      await mutateAsync({ data: formData });
      setFiles(undefined);
      onUpdate();
    }
  };

  const handleSubmitClick = () => {
    form.handleSubmit(handleSubmit)();
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={true}
      fullWidth={true}
      maxWidth={'xl'}
      // onClose={onClosed}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      <DialogTitle id="dialog-title" style={styling.dialogTitleStyle}>
        UPDATE FILE
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClosed}
        sx={{
          position: 'absolute',
          right: 10,
          top: 10,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <ReactHookForm formName="SAVE_CONTACT_FORM" onSubmit={handleSubmit} formContextProps={form}>
        <DialogContent dividers style={styling.contentPadding} className="popupDilog">
          <LoadingIndicator isLoading={isLoading} />
          <Grid container spacing={2}>
            <Grid item lg={6} sm={6} xl={6} xs={6}>
              <div style={styling.dropZone} {...getRootProps()}>
                <input {...getInputProps()} />
                {isDragActive ? (
                  <p>Drop the files here ...</p>
                ) : (
                  <div>
                    {files ? (
                      <p>{`${files.name} are selected to upload`}</p>
                    ) : fileData ? (
                      <p>{`${fileData?.fileName} are selected to upload`}</p>
                    ) : (
                      <p>Drag and drop a file here, or click to select a file</p>
                    )}
                  </div>
                )}
              </div>
            </Grid>
            <Grid item lg={6} sm={6} xl={6} xs={6}>
              <FormInput label="Description" name="description" multiline={true} size={2.5} />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <div className="spaceBetween">
                <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
                  Cancel
                </Button>
                <Button type={'button'} variant="contained" onClick={handleSubmitClick} style={{ width: '130px' }}
                  disabled={isReadOnlyPermission}>
                  Save
                </Button>
              </div>
            </Grid>
          </Grid>
        </DialogContent>
      </ReactHookForm>
    </Dialog>
  );
};
